import { Box, VStack } from '@chakra-ui/layout';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  IconButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { ExperienceValues } from '@common/models/InvestorAccountModel/types';
import React, { useState } from 'react';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { useSurveyBannerModel } from '../models/SurveyBannerModel';

const SELECTOR_FONT_SIZE = '12px';
type ExperienceSelectorProps = {
  onSelect: (value: number) => void;
  selected: number | null;
};
const ExperienceSelector: React.FC<ExperienceSelectorProps> = ({ selected, onSelect }) => {
  return (
    <Box w={'100%'}>
      <Flex w={'100%'}>
        <Flex
          alignItems="center"
          backgroundColor={selected === 1 ? 'gray.50' : 'transparent'}
          borderColor="gray.50"
          borderLeftRadius="10px"
          borderWidth={2}
          cursor="pointer"
          flex={1}
          flexDirection="column"
          justifyContent="center"
          onClick={() => {
            onSelect(1);
          }}
          py="8px">
          <TextBlock lineHeight={SELECTOR_FONT_SIZE} weight="bold" fontSize={SELECTOR_FONT_SIZE}>
            None
          </TextBlock>
          {selected === 1 && (
            <Icon
              borderRadius={'20px'}
              backgroundColor={'white'}
              fontSize={'14px'}
              mb={'-16px'}
              icon="circle-check"
              color={'green.300'}
            />
          )}
        </Flex>
        <Flex
          alignItems="center"
          backgroundColor={selected === 3 ? 'gray.50' : 'transparent'}
          borderBottomWidth={2}
          borderColor="gray.50"
          borderTopWidth={2}
          cursor="pointer"
          flex={1}
          flexDirection="column"
          justifyContent="center"
          onClick={() => {
            onSelect(3);
          }}
          py="8px">
          <TextBlock lineHeight={SELECTOR_FONT_SIZE} weight="bold" fontSize={SELECTOR_FONT_SIZE}>
            Limited
          </TextBlock>
          {selected === 3 && (
            <Icon
              borderRadius={'20px'}
              backgroundColor={'white'}
              fontSize={'14px'}
              mb={'-16px'}
              icon="circle-check"
              color={'green.300'}
            />
          )}
        </Flex>
        <Flex
          alignItems="center"
          cursor="pointer"
          backgroundColor={selected === 5 ? 'gray.50' : 'transparent'}
          borderColor="gray.50"
          borderRightRadius="10px"
          borderWidth={2}
          flexDirection="column"
          flex={1}
          justifyContent="center"
          onClick={() => {
            onSelect(5);
          }}
          py="8px">
          <TextBlock lineHeight={SELECTOR_FONT_SIZE} weight="bold" fontSize={SELECTOR_FONT_SIZE}>
            Experienced
          </TextBlock>
          {selected === 5 && (
            <Icon
              borderRadius={'20px'}
              backgroundColor={'white'}
              fontSize={'14px'}
              mb={'-16px'}
              icon="circle-check"
              color={'green.300'}
            />
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

type ExperienceProps = {
  modalProps: UseDisclosureProps;
};

export const Experience: React.FC<ExperienceProps> = ({ modalProps }) => {
  const model = useSurveyBannerModel();
  const { investorAccount, updateExperience } = useInvestorAccountModel();
  const [showError, setShowError] = useState(false);
  const [experience, setExperience] = useState<ExperienceValues>({
    alternatives: investorAccount?.non_accredited_survey_v2?.experience?.experience_alts?.value || null,
    bonds: investorAccount?.non_accredited_survey_v2?.experience?.experience_bonds?.value || null,
    realEstate: investorAccount?.non_accredited_survey_v2?.experience?.experience_re?.value || null,
    stocks: investorAccount?.non_accredited_survey_v2?.experience?.experience_stocks?.value || null,
  });

  return (
    <ModalContent>
      <ModalHeader
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        borderBottomWidth={'1px'}
        borderBottomColor={'gray.300'}>
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="chevron-left" />}
          onClick={() => model.prevStep()}
        />
        Experience
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="times" />}
          onClick={modalProps.onClose}
        />
      </ModalHeader>
      <ModalBody>
        <Flex flex={1} py="24px" justifyContent={'space-between'} flexDirection={'column'} display={'flex'}>
          <VStack gap={'8px'}>
            <Box>
              <TextBlock fontSize={['14px', '14px', '20px']} maxW={'300px'} textAlign="center" weight="bold">
                {'How would you rate your investment experience?'}
              </TextBlock>
            </Box>
            <Box w={'100%'}>
              <TextBlock fontSize={'16px'} weight="bold" mb="10px" textAlign="center">
                Stocks
              </TextBlock>
              <ExperienceSelector
                onSelect={(value) => {
                  setExperience({ ...experience, stocks: value });
                }}
                selected={experience.stocks}
              />
            </Box>
            <Box w={'100%'}>
              <TextBlock fontSize={'16px'} weight="bold" mb="10px" textAlign="center">
                Bonds
              </TextBlock>
              <ExperienceSelector
                onSelect={(value) => {
                  setExperience({ ...experience, bonds: value });
                }}
                selected={experience.bonds}
              />
            </Box>

            <Box w={'100%'}>
              <TextBlock fontSize={'16px'} weight="bold" mb="10px" textAlign="center">
                Real Estate
              </TextBlock>
              <ExperienceSelector
                onSelect={(value) => {
                  setExperience({ ...experience, realEstate: value });
                }}
                selected={experience.realEstate}
              />
            </Box>

            <Box w={'100%'}>
              <TextBlock fontSize={'16px'} weight="bold" mb="10px" textAlign="center">
                Alternatives
              </TextBlock>

              <ExperienceSelector
                onSelect={(value) => {
                  setExperience({ ...experience, alternatives: value });
                }}
                selected={experience.alternatives}
              />
            </Box>
          </VStack>

          {showError && (
            <Alert status="error">
              <AlertIcon />
              <AlertDescription>We were unable to update your profile. Please try again</AlertDescription>
            </Alert>
          )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          autoFocus={true}
          isLoading={model.loading}
          isDisabled={Object.values(experience).some((item) => item === null)}
          w={'100%'}
          rightIcon={<Icon icon="chevron-right" />}
          mt={'10px'}
          onClick={async () => {
            setShowError(false);
            if (Object.values(experience).every((item) => item != null)) {
              let success = true;
              model.setLoading(true);
              success = (await updateExperience(experience)).success;
              if (success) {
                model.nextStep();
                model.setLoading(false);
              } else {
                setShowError(true);
                model.setLoading(false);
              }
            }
          }}>
          Continue
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};
